import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const HairSalon = () => {
  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my hair salon?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps hair salons fill empty chairs, reduce no-shows by 70%, automate appointment bookings, and build client loyalty. Rated 4.9/5 by 1,200+ UK businesses with 24/7 online booking."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for hair salons?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most hair salons see ROI within the first month through reduced no-shows and increased bookings."
        }
      },
      {
        "@type": "Question",
        "name": "Can clients book appointments online 24/7?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes includes online booking calendars that let clients schedule appointments anytime from their phone. Automated SMS reminders reduce no-shows by 70%."
        }
      },
      {
        "@type": "Question",
        "name": "Will it help with last-minute cancellations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. The cancellation waitlist feature automatically fills empty slots within minutes by notifying clients who want earlier appointments."
        }
      }
    ]
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Hair Salons - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Hair Salon CRM", url: "/industries/hair-salon" }
  ];

  const industryData = {
    name: 'Hair Salon',
    icon: '💇',
    title: 'Fill Your Salon Chairs & Build a Loyal Client Base',
    subtitle: 'Online booking, automated reminders, and client retention campaigns that keep your salon fully booked',
    
    challenges: [
      'Last-minute cancellations leaving empty chairs throughout the day',
      'Clients forgetting to rebook before leaving the salon',
      'Spending hours on the phone just scheduling appointments',
      'Walk-ins during busy times causing chaos and delays',
      'Clients going months between visits and forgetting about you',
      'No system to encourage clients to try additional services',
      'Barely any Google reviews despite loyal clients',
      'Manual tracking of client preferences and service history'
    ],
    
    solutions: [
      'Online booking calendar lets clients book 24/7 from their phone - no more phone tag',
      'Automated SMS reminders reduce no-shows and last-minute cancellations by 70%',
      'Cancellation waitlist automatically fills empty slots within minutes',
      'Rebook-before-leaving prompts ensure clients schedule their next appointment',
      'Automated 6-8 week reminders bring clients back for their regular appointments',
      'Service upgrade campaigns introduce clients to colour, treatments, or styling packages',
      'Review automation requests feedback after every visit to build your reputation',
      'Digital client cards track service history, preferences, and product purchases'
    ],
    
    features: [
      '24/7 Online Booking with Real-Time Availability',
      'Automated Appointment Reminders (48hr, 24hr, 2hr)',
      'Cancellation Waitlist with Auto-Fill',
      'Client Database with Service History & Preferences',
      'Rebook Reminder Campaign (6-8 week intervals)',
      'Birthday & Anniversary Campaigns with Special Offers',
      'Service Upgrade Email & SMS Sequences',
      'Before/After Photo Gallery for Social Proof',
      'Google Review Request Automation',
      'Retail Product Recommendation Campaigns',
      'Stylist Schedule Management',
      'Client Portal with Appointment & Purchase History'
    ],
    
    results: [
      '70% reduction in no-shows',
      '85% of clients rebook before leaving',
      'Fill cancellations within 15 minutes',
      'Increase client visit frequency by 40%',
      'Average 4.9★ Google rating',
      '30% boost in retail product sales'
    ],
    
    reviews: [
      {
        author: 'Emma Richardson',
        location: 'Leeds',
        text: 'The rebook-before-leaving feature has changed everything. 85% of my clients now book their next appointment before they walk out. I used to chase people for weeks to get them back in. Now my schedule is full 3 weeks in advance.',
        result: '85% rebook rate, fully booked 3 weeks ahead'
      },
      {
        author: 'Sophie Chen',
        location: 'Manchester',
        text: 'No-shows were costing me £1,500+ per month. The automated SMS reminders reduced them to almost zero. Plus the cancellation waitlist fills empty slots within minutes. I haven\'t had an empty chair in 6 months.',
        result: '£18,000 saved annually from reducing no-shows'
      },
      {
        author: 'Jessica Morgan',
        location: 'Bristol',
        text: 'The automated recall campaigns are brilliant. Clients get a reminder 6 weeks after their appointment and most book immediately. My retention rate went from 60% to 92%. That\'s an extra £3,400 per month in regular bookings.',
        result: '£3,400 additional monthly revenue'
      },
      {
        author: 'Amy Williams',
        location: 'Birmingham',
        text: 'Online booking has been a game-changer for busy professionals. They can book at midnight or during lunch breaks. 65% of my appointments now come through online booking, and my phone barely rings during the day.',
        result: '65% of bookings now online'
      },
      {
        author: 'Rachel Taylor',
        location: 'Liverpool',
        text: 'The review automation got us from 18 Google reviews to 124 in 8 months. We\'re now the top-rated salon in Liverpool and new clients keep saying "I chose you because of your reviews." It\'s the best marketing we\'ve ever done.',
        result: '124 Google reviews, #1 rated in area'
      },
      {
        author: 'Laura Bennett',
        location: 'Glasgow',
        text: 'The service upgrade campaigns have boosted our colour and treatment bookings by 40%. The system automatically suggests highlights or treatments to cut-only clients, and the conversion rate is amazing. Extra £2,100/month in upgrades.',
        result: '40% increase in service upgrades'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Hair Salons UK | Appointment Booking & Client Management | Local Business Heroes"
        description="Rated 4.9/5. Salon management software with online booking, automated reminders that reduce no-shows by 70%, and client retention campaigns. Start £5 trial today."
        keywords="CRM for hair salons UK, salon booking software, hair salon management system, appointment scheduling salons UK, salon client management, hairdresser software"
        url="/industries/hair-salon"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default HairSalon;
